/* How to get each watch's export, and what it adds on top of Strava. Steps come from each vendor's
 * help pages as researched for the plan (docs/plan.md, "Other watches and apps"); re-check them when a
 * vendor changes its site, and update CHECKED. Strava is always the main source; a watch is optional. */
export const CHECKED = 'October 2026';

export interface WatchSource {
  id: string;
  name: string;
  supported: 'yes' | 'files' | 'no'; // whole export / individual run files / not yet
  adds: string[];
  steps: string[];
  note?: string;
}

const FIT_ADDS = [
  'Runs that never reached Strava',
  'Heart rate, cadence, hills and splits for runs Strava only has as a summary',
];

export const WATCHES: WatchSource[] = [
  {
    id: 'garmin', name: 'Garmin', supported: 'yes',
    adds: ['Runs that never reached Strava', 'Run/walk detection: walk breaks and how long you ran between them', 'Your watch’s VO2max estimate over time'],
    steps: [
      'Sign in at garmin.com → Account → Data Management → Export Your Data.',
      'Request the export. Garmin emails a link, often within a couple of days.',
      'Drop every ZIP Garmin sends here, next to your Strava ZIP.',
    ],
  },
  {
    id: 'coros', name: 'COROS', supported: 'yes', adds: FIT_ADDS,
    steps: [
      'On a computer, sign in to COROS Training Hub → Activity List → Export Data.',
      'Choose FIT. COROS emails a download link.',
      'Drop the ZIP here, next to your Strava ZIP.',
    ],
  },
  {
    id: 'polar', name: 'Polar', supported: 'files', adds: FIT_ADDS,
    steps: [
      'On flow.polar.com, open a run and choose Export session → FIT.',
      'Repeat for the runs you want (there’s no bulk FIT download).',
      'Drop the .fit files here, next to your Strava ZIP.',
    ],
    note: 'Polar’s full account download (account.polar.com) isn’t supported yet.',
  },
  {
    id: 'suunto', name: 'Suunto', supported: 'files', adds: FIT_ADDS,
    steps: [
      'In the Suunto app, open a run → ⋯ → Export, and choose FIT.',
      'Repeat for the runs you want (Suunto has no self-service bulk export).',
      'Drop the .fit files here, next to your Strava ZIP.',
    ],
  },
  {
    id: 'apple', name: 'Apple Watch', supported: 'yes',
    adds: ['Runs that never reached Strava', 'Cadence, from your watch’s step counts', 'Apple’s VO2max estimate (Cardio Fitness) over time', 'Heart-rate zones and the weather on each run'],
    steps: [
      'On your iPhone, open the Health app and tap your picture (top right).',
      'Tap Export All Health Data, then Export. It can take a few minutes.',
      'Save export.zip to Files or AirDrop it to your computer, then drop it here next to your Strava ZIP.',
    ],
    note: 'The file can be several GB; a computer handles it best. Only your running workouts and their heart rate, steps and distance are read.',
  },
  {
    id: 'other', name: 'Other (Wahoo, Amazfit, …)', supported: 'files', adds: FIT_ADDS,
    steps: [
      'Export your runs as .fit files from your watch’s app or website.',
      'Drop the .fit files (or a ZIP of them) here, next to your Strava ZIP.',
    ],
  },
];
